/**
 * Wielkości zestawu potrzebne do VoltValue Projektu: moc, ceny, pojemność magazynu.
 *
 * Wspólne dla panelu w asystencie i dla przycisku na kolejnych krokach ścieżki —
 * te same liczby wyliczane dwa razy rozjechałyby się przy pierwszej zmianie
 * w kształcie odpowiedzi z API.
 *
 * Moc bierzemy z `summary.panels_total_kwp`, gdy jest: to liczba z silnika doboru,
 * a nie z mnożenia w przeglądarce. Dopiero gdy jej brak, sięgamy po wynik doboru
 * elektrycznego, a na końcu po moc modułu razy sztuki.
 */
/**
 * Typowe napięcia pracy magazynów domowych — ZAPASOWY opis na schemacie.
 *
 * Używane tylko wtedy, gdy dla modelu nie mamy karty katalogowej. Gdy karta
 * jest, na schemat idą liczby konkretnego magazynu (patrz `napiecieMagazynu`),
 * bo wartość typowa dla klasy urządzeń a rzeczywiste okno pracy to dwie różne
 * rzeczy i instalator musi wiedzieć, którą widzi.
 *
 * Niskonapięciowy: 16 ogniw LiFePO4 szeregowo, 3,2 V każde, czyli 51,2 V nominalnie;
 * w praktyce mówi się o systemach 48 V, a napięcie pracy waha się ok. 40–58 V.
 * Wysokonapięciowy: moduły łączone szeregowo w stos, typowo od ok. 150 do 600 V DC.
 */
export const NAPIECIA_MAGAZYNU = {
  wysokie: { etykieta: 'Wysokonapięciowy', zakres: 'typowo 150–600 V DC' },
  niskie: { etykieta: 'Niskonapięciowy', zakres: 'typowo 48 V DC (ok. 40–58 V)' },
}

const liczba = (v) => (v == null || v === '' || Number.isNaN(Number(v)) ? null : Number(v))
const bezZera = (v) => String(v).replace('.', ',')

/**
 * Opis napięcia magazynu na schemat: z karty katalogowej, gdy ją mamy.
 *
 * `zKarty` to `electrical_spec.values` modelu magazynu. Bierzemy okno pracy,
 * a gdy karta go nie podaje (przy ośmiu z trzynastu kart sterowników okno
 * zależy od liczby modułów i celowo zostało puste) — samo napięcie nominalne.
 * Dopiero gdy nie ma nic, wracamy do wartości typowej dla klasy.
 *
 * `zrodlo` jedzie razem z liczbami, bo schemat trafia do PDF-a dla instalatora:
 * „44,8–57,6 V z karty" i „typowo 48 V" to dla niego zupełnie inna informacja.
 */
export const napiecieMagazynu = (voltage, zKarty = null) => {
  const klasa = klasaNapieciaMagazynu(voltage)
  const vmin = liczba(zKarty?.voltage_min_v)
  const vmax = liczba(zKarty?.voltage_max_v)
  const vnom = liczba(zKarty?.voltage_nominal_v)

  if (vmin != null && vmax != null) {
    return {
      etykieta: klasa?.etykieta || 'Magazyn',
      zakres: `${bezZera(vmin)}–${bezZera(vmax)} V DC`,
      nominalne: vnom != null ? `${bezZera(vnom)} V nom.` : null,
      zrodlo: 'karta',
    }
  }
  if (vnom != null) {
    return {
      etykieta: klasa?.etykieta || 'Magazyn',
      zakres: `${bezZera(vnom)} V DC nominalnie`,
      nominalne: null,
      zrodlo: 'karta',
    }
  }
  return klasa ? { ...klasa, nominalne: null, zrodlo: 'klasa' } : null
}

/** Rozpoznanie po polskiej nazwie z katalogu (`spec.voltage`), a nie po skrócie HV/LV. */
export const klasaNapieciaMagazynu = (voltage) => {
  const t = String(voltage || '').toLowerCase()
  if (t.includes('wysoko')) return NAPIECIA_MAGAZYNU.wysokie
  if (t.includes('nisko')) return NAPIECIA_MAGAZYNU.niskie
  return null
}

/**
 * Pojemność WSZYSTKICH magazynów zestawu w kWh albo null. Silnik doboru podaje w `battery_capacity_kwh` jedną sztukę,
 * a sumę w `battery_capacity_total_kwh` (wykryte 2026-10-30: dwa magazyny 10 kWh wyglądały jak 10 kWh, a montaż i marża
 * liczyły się od jednej sztuki). Bez sumy: pozycje razy liczba sztuk, a bez nich samo to pole (starsze zapisy bez pozycji).
 */
export const pojemnoscZestawuKwh = (summary, items) => {
  const suma = Number(summary?.battery_capacity_total_kwh)
  if (suma > 0) return suma
  const zPozycji = (Array.isArray(items) ? items : []).filter((i) => i?.category === 'battery')
    .reduce((s, i) => s + (Number(i.spec?.capacity_kwh) || 0) * (Number(i.quantity) || 1), 0)
  if (zPozycji > 0) return Math.round(zPozycji * 100) / 100
  const pole = Number(summary?.battery_capacity_kwh)
  return pole > 0 ? pole : null
}

export const podsumujZestaw = (items, summary, mocZeSchematu) => {
  const panel = (items || []).find((i) => i.category === 'panels')
  const falownik = (items || []).find((i) => i.category === 'inverter')
  const bateria = (items || []).find((i) => i.category === 'battery')
  const kosztProduktow = (items || []).reduce((s, i) => s + (i.line_price_pln || 0), 0)

  const zModulow = panel?.spec?.power_w && panel?.quantity
    ? Math.round((panel.spec.power_w * panel.quantity) / 10) / 100
    : null

  const pojemnosc = pojemnoscZestawuKwh(summary, items) || 0

  return {
    // Lista pozycji PRZECHODZI DALEJ, mimo że to podsumowanie: strona
    // „Zastosowane komponenty" w dokumencie projektu potrzebuje zdjęć, nazw
    // i parametrów każdego elementu. Bez tego dostawała `undefined` i po cichu
    // się nie renderowała — a wołający ma tu jedno źródło danych o zestawie,
    // nie dwa.
    items: items || [],
    kwp: summary?.panels_total_kwp || mocZeSchematu || zModulow,
    kosztProduktowZl: Math.round(kosztProduktow) || null,
    kosztFalownikaZl: falownik?.line_price_pln ? Math.round(falownik.line_price_pln) : null,
    pojemnoscMagazynuKwh: pojemnosc,
    magazyn: pojemnosc > 0
      ? {
        pojemnoscKwh: pojemnosc,
        nazwa: bateria?.model_name || null,
        // `modelId` jest tu po to, żeby okno schematu mogło dociągnąć kartę
        // katalogową magazynu — sama odpowiedź doboru napięć nie niesie.
        modelId: bateria?.model_id || null,
        klasaNapiecia: bateria?.spec?.voltage || null,
        napiecie: napiecieMagazynu(bateria?.spec?.voltage),
      }
      : null,
  }
}

export default podsumujZestaw
